// =====================================================================================
// INVRT Radar - web console - HIGH-INTENT Leads search (Story N3)
//
// The pool-wide search under the binding law: state, role, industry keywords, tag, intent
// band and an optional campaign scope, against GET /v1/radar/search. Filters are applied
// explicitly (Apply filters), pagination follows the seam's opaque cursor, and a filtered
// miss is worded differently from an empty pool. A 403 renders the permission-denied
// state, never a crash: the server, not this screen, decides scope.
//
// The intent band values (high / warm / standard) are this screen's presentation of the
// scoring thresholds; the parameter is passed to the seam verbatim.
// =====================================================================================

import { useEffect, useMemo, useState } from "react";
import { isApiError } from "@/lib/api";
import { EmptyState, ErrorState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Select } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui/table";
import type { RadarLead } from "@/lib/types.radar";
import {
  EMPTY_LEAD_FILTERS,
  LEADS_PAGE_LIMIT,
  hasLeadFilters,
  useRadarCampaigns,
  useRadarLeadsPage,
  type RadarLeadFilters,
} from "./useRadar";

function intentTone(band: string | null): "success" | "info" | "neutral" {
  if (band === "high") return "success";
  if (band === "warm") return "info";
  return "neutral";
}

function leadTitle(lead: RadarLead): string {
  return lead.company ?? lead.contact_name ?? "Unnamed lead";
}

export function LeadsSearch() {
  const campaigns = useRadarCampaigns();

  // The draft filters are what the inputs hold; the applied filters are what the query
  // runs with, so typing never fires a request and Apply is one honest commit.
  const [draft, setDraft] = useState<RadarLeadFilters>(EMPTY_LEAD_FILTERS);
  const [applied, setApplied] = useState<RadarLeadFilters>(EMPTY_LEAD_FILTERS);
  // The cursor stack: index i holds the cursor page i starts at (page 0 has none).
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [page, setPage] = useState(0);

  const queryText = useMemo(() => JSON.stringify(applied), [applied]);
  useEffect(() => {
    setCursors([undefined]);
    setPage(0);
  }, [queryText]);

  const results = useRadarLeadsPage(applied, cursors[page]);

  function applyFilters(): void {
    setApplied(draft);
  }

  function clearFilters(): void {
    setDraft(EMPTY_LEAD_FILTERS);
    setApplied(EMPTY_LEAD_FILTERS);
  }

  function goNext(): void {
    const next = results.data?.next_cursor;
    if (next === null || next === undefined) return;
    setCursors((current) => {
      const stack = [...current];
      stack[page + 1] = next;
      return stack;
    });
    setPage(page + 1);
  }

  function goPrevious(): void {
    if (page === 0) return;
    setPage(page - 1);
  }

  const rows = results.data?.leads ?? [];
  const filtered = hasLeadFilters(applied);
  const denied = results.isError && isApiError(results.error) && results.error.httpStatus === 403;

  return (
    <section aria-labelledby="hi-leads-heading" className="flex flex-col gap-5">
      <div>
        <h2 id="hi-leads-heading" className="text-lg font-semibold text-ink">
          Leads search
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Search the whole HIGH-INTENT pool, not one campaign at a time. Results are capped
          at {LEADS_PAGE_LIMIT} leads per page.
        </p>
      </div>

      <form
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        aria-label="Lead filters"
        onSubmit={(event) => {
          event.preventDefault();
          applyFilters();
        }}
      >
        <Input
          label="State"
          className="h-11"
          value={draft.state}
          onChange={(event) => setDraft({ ...draft, state: event.target.value })}
          placeholder="TX"
          autoComplete="off"
        />
        <Input
          label="Role"
          className="h-11"
          value={draft.role}
          onChange={(event) => setDraft({ ...draft, role: event.target.value })}
          placeholder="director"
          autoComplete="off"
        />
        <Input
          label="Industry"
          className="h-11"
          value={draft.industry}
          onChange={(event) => setDraft({ ...draft, industry: event.target.value })}
          placeholder="solar"
          autoComplete="off"
          hint="Keywords matched against the lead's industry."
        />
        <Input
          label="Tag"
          className="h-11"
          value={draft.tag}
          onChange={(event) => setDraft({ ...draft, tag: event.target.value })}
          placeholder="A"
          autoComplete="off"
        />
        <Select
          label="Intent band"
          className="h-11"
          value={draft.intentBand}
          onChange={(event) => setDraft({ ...draft, intentBand: event.target.value })}
        >
          <option value="">All bands</option>
          <option value="high">High</option>
          <option value="warm">Warm</option>
          <option value="standard">Standard</option>
        </Select>
        <Select
          label="Campaign"
          className="h-11"
          value={draft.campaignId}
          onChange={(event) => setDraft({ ...draft, campaignId: event.target.value })}
          hint="Optional: scope the search to one campaign's slice of the pool."
        >
          <option value="">All campaigns</option>
          {(campaigns.data?.campaigns ?? []).map((campaign) => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name ?? campaign.code ?? campaign.id}
            </option>
          ))}
        </Select>
        <div className="flex items-end gap-3">
          <Button type="submit" className="h-11">Apply filters</Button>
          <Button type="button" variant="secondary" className="h-11" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      </form>

      {results.isLoading ? <TableSkeleton label="Loading leads…" rows={LEADS_PAGE_LIMIT} columns={5} /> : null}
      {results.isError && !denied ? (
        <ErrorState error={results.error} onRetry={() => void results.refetch()} />
      ) : null}
      {denied ? (
        <EmptyState
          title="You do not have permission to view these leads."
          body="The server refused this search for the selected organisation."
        />
      ) : null}

      {results.isSuccess ? (
        rows.length === 0 ? (
          filtered ? (
            <EmptyState
              title="No leads match these filters"
              body="Widen the filters or clear them to see the whole pool."
              action={
                <Button type="button" variant="secondary" className="h-11" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="No leads in the pool yet"
              body="Leads appear here once the analysis worker starts filling the pool for your campaigns."
            />
          )
        ) : (
          <div className="flex flex-col gap-4">
            <div className="hidden overflow-x-auto md:block">
              <Table aria-label="Leads desktop view">
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Lead</TableHeaderCell>
                    <TableHeaderCell>Role</TableHeaderCell>
                    <TableHeaderCell>State</TableHeaderCell>
                    <TableHeaderCell>Tag</TableHeaderCell>
                    <TableHeaderCell>Intent</TableHeaderCell>
                    <TableHeaderCell className="text-right">Score</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <p className="font-medium">{leadTitle(lead)}</p>
                        {lead.contact_name && lead.company ? (
                          <p className="text-xs text-ink-muted">{lead.contact_name}</p>
                        ) : null}
                        {lead.industry ? <p className="text-xs text-ink-muted">{lead.industry}</p> : null}
                      </TableCell>
                      <TableCell>{lead.role ?? "-"}</TableCell>
                      <TableCell>{lead.state ?? "-"}</TableCell>
                      <TableCell>{lead.tag ?? "-"}</TableCell>
                      <TableCell>
                        <Badge tone={intentTone(lead.intent_band)}>{lead.intent_band ?? "unknown"}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {lead.score === null ? "-" : lead.score}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <ul aria-label="Leads mobile view" className="grid gap-3 md:hidden">
              {rows.map((lead) => (
                <li key={lead.id}>
                  <Card>
                    <CardHeader className="flex-row items-start justify-between gap-3">
                      <div>
                        <CardTitle>{leadTitle(lead)}</CardTitle>
                        {lead.role ? <p className="mt-1 text-xs text-ink-muted">{lead.role}</p> : null}
                      </div>
                      <Badge tone={intentTone(lead.intent_band)}>{lead.intent_band ?? "unknown"}</Badge>
                    </CardHeader>
                    <CardContent className="flex flex-wrap gap-2">
                      {lead.state ? <Badge tone="neutral">State: {lead.state}</Badge> : null}
                      {lead.tag ? <Badge tone="neutral">Tag: {lead.tag}</Badge> : null}
                      {lead.score !== null ? <Badge tone="info">Score: {lead.score}</Badge> : null}
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>

            <Pagination
              page={page}
              hasPrevious={page > 0}
              hasNext={(results.data?.next_cursor ?? null) !== null}
              onPrevious={goPrevious}
              onNext={goNext}
            />
          </div>
        )
      ) : null}
    </section>
  );
}
